import { concat as concat$, from as from$, of as of$ } from 'rxjs';
import { catchError, filter, map, mergeMap, switchMap, takeUntil } from 'rxjs/operators';

import { ETA_TYPES, RequestStatus } from '../../../enums';
import { getRequestedArrivalTime, parseRequestValues, parseStatus } from '../../../parsers/request.parser';
import { buildSchedulePayload } from '../../../parsers/schedule.parser';
import * as errorActions from '../../../redux/actions/error.actions';
import * as etaActions from '../../../redux/actions/eta.actions';
import * as requestsActions from '../../../redux/actions/requests.actions';
import { errorPipe } from '../../../redux/epics/epicsUtil';
import { getRequestId, updateRequest, upsertSchedule } from '../../../redux/epics/updateRequestUtil';
import RequestService from '../../../services/request.service';

export const getETAUpdateRequestEpic = (action$, store) =>
  action$.pipe(
    filter(etaActions.requestETA.match),
    filter(() => store.value.eta.etaType === ETA_TYPES.PICKUP),
    filter(() => getRequestId(store)),
    switchMap(() => from$(upsertSchedule(store.value))),
    switchMap(schedule => {
      const isGettingNewEta = store.value.formFlow.pendingRequestUpdate;
      const activatedReturnRequestId = store.value.requests.isJustActivated
        ? store.value.requests.request.returnRequestId
        : null;
      let updateRequest$;
      if (isGettingNewEta) {
        updateRequest$ = from$(
          updateRequest(schedule, store.value, {
            ...getRequestedArrivalTime(store.value, true),
            ...parseStatus(store.value),
          }),
        );
      } else {
        updateRequest$ = from$(updateRequest(schedule, store.value, parseRequestValues(store.value, true)));
      }
      return updateRequest$.pipe(
        switchMap(request => {
          const scheduleSuccess = etaActions.asyncUpsertScheduleSuccess({
            request: { ...request, scheduleId: schedule.id },
            schedule,
          });
          if (!activatedReturnRequestId) {
            return of$(scheduleSuccess);
          }

          const requestedArrivalTime = store.value.requests.request.returnTripArrivalTime(
            store.value.eta.requestedArrivalTime,
          );
          return from$(
            RequestService.updateRequest(activatedReturnRequestId, {
              status: RequestStatus.NEEDS_ASSIGNMENT,
              requestedArrivalTime,
            }),
          ).pipe(
            mergeMap(returnRequest =>
              concat$(of$(requestsActions.updateReturnRequestSuccess(returnRequest)), of$(scheduleSuccess)),
            ),
            catchError(error =>
              concat$(of$(errorActions.onError({ error })), of$(etaActions.requestETAinProgress(false))),
            ),
          );
        }),
        takeUntil(
          action$.pipe(
            filter(
              action =>
                etaActions.asyncUpsertScheduleSuccess.match(action) || etaActions.requestETATimeout.match(action),
            ),
          ),
        ),
        errorPipe,
      );
    }),
  );

export const getAppmtAutoETAEpic = (action$, store) => {
  let requestedArrivalTime;
  return action$.pipe(
    filter(etaActions.requestETA.match),
    filter(() => store.value.eta.etaType === ETA_TYPES.APPOINTMENT),
    filter(() => getRequestId(store) && !store.value.formFlow.etaWasAccepted),
    switchMap(() => from$(RequestService.getRequestedArrivalTime(getRequestId(store), store.value))),
    switchMap(response => {
      requestedArrivalTime = response.estimatedArrivalTime;
      return from$(
        upsertSchedule(store.value, {
          ...buildSchedulePayload(store.value),
          ...{ pickupTime: requestedArrivalTime },
        }),
      );
    }),
    switchMap(schedule =>
      from$(
        updateRequest(schedule, store.value, {
          requestedArrivalTime,
          requestedAppointmentTime: store.value.eta.requestedAppointmentTime,
        }),
      ).pipe(
        map(request =>
          etaActions.asyncUpsertScheduleSuccess({
            request: { ...request, scheduleId: schedule.id },
            schedule,
          }),
        ),
        errorPipe,
      ),
    ),
  );
};
